import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import {
    Box,
    Button, IconButton, Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow, Tooltip,
    Typography,
} from '@mui/material';
import { Toc } from '@mui/icons-material';
import Currency from 'components/formaters/Currency';
import Date from 'components/formaters/Date';
import { InvoiceModel, InvoiceStatus } from 'models/InvoiceModel';
import { CompanyType } from 'models/CompanyModel';
import useInvoices from 'modules/invoices/hooks/useInvoices';
import useLedgerModals from 'modules/invoices/hooks/useLedgerModals';
import { InvoiceLink } from 'modules/invoices/common/Components';
import auth from 'recoil/atoms/auth';


const BalanceAndInvoices = () => {
    const [unpaid, setUnpaid] = useState<InvoiceModel[]>();
    const [pending, setPending] = useState<InvoiceModel[]>();
    const my = useRecoilValue(auth);
    const {t} = useTranslation();
    const {paidInvoice, findInvoices} = useInvoices();
    const {openTransactions} = useLedgerModals();

    const load = () => {
        findInvoices({
            status: InvoiceStatus.Unpaid,
            payer: 'shipper',
        })
            .then(res => {
                const unp: InvoiceModel[] = [], pen: InvoiceModel[] = [];

                res.data.forEach((inv: InvoiceModel) => {
                    if (inv.status === InvoiceStatus.Unpaid)
                        unp.push(inv)
                    else
                        pen.push(inv)
                });
                setUnpaid(unp);
                setPending(pen);
            });
    };

    // eslint-disable-next-line
    useEffect(() => load(), []);

    if (!unpaid) return null;

    const paid = (id: number) => paidInvoice(id).then(load);

    return (
        <Paper
            sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                height: 400,
            }}
        >
            {my.type === CompanyType.Shipper ?
                <>
                    <Typography component="h2" variant="h6" color="primary" gutterBottom>
                        {t('balance_and_unpaid_invoices')}
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}>
                        <Typography component="p" variant="h3">
                            <Box
                                component="span"
                                sx={{color: my.company.freeBalance < 0 ? 'error.light' : undefined}}
                            >
                                <Currency value={my.company.freeBalance}/>
                            </Box>
                        </Typography>

                        <Tooltip
                            title={t('transactions')}
                            placement="top"
                        >
                            <IconButton
                                onClick={() => openTransactions(my.company)}
                            >
                                <Toc/>
                            </IconButton>
                        </Tooltip>
                    </Box>
                </> :
                <Typography component="h2" variant="h6" color="primary" gutterBottom>
                    {t('unpaid_invoices')}
                </Typography>
            }

            <TableContainer>
                <Table
                    size="small"
                    aria-label="invoice table"
                    sx={{
                        '& .MuiTableCell-sizeSmall': {
                            p: '6px',
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('invoice_no')}</TableCell>
                            <TableCell></TableCell>
                            <TableCell
                                sx={{textAlign: 'right'}}
                            >
                                {t('amount')}
                            </TableCell>
                            <TableCell>{t('due_date')}</TableCell>
                            <TableCell> </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {unpaid.map(i => (
                            <TableRow
                                key={i.id}
                                sx={{
                                    '&:last-child td, &:last-child th': {border: 0},
                                }}
                            >
                                <TableCell>
                                    <InvoiceLink invoice={i}/>
                                </TableCell>
                                <TableCell>
                                    {i.type === 'freight' && (
                                        <Link href={`/voyage/${i.voyage.id}/finances`}>{i.voyage.number}</Link>
                                    )}
                                </TableCell>
                                <TableCell
                                    sx={{textAlign: 'right'}}
                                >
                                    <Currency value={i.grandTotal} currency={i.currency || undefined}/>
                                </TableCell>
                                <TableCell><Date date={i.dueDate}/></TableCell>

                                {my.type === CompanyType.Shipper ?
                                    <TableCell
                                        sx={{textAlign: 'right'}}
                                    >
                                        <Button onClick={() => paid(i.id)}>I paid</Button>
                                    </TableCell> :
                                    <TableCell>
                                        {i.payer.name}
                                    </TableCell>
                                }
                            </TableRow>
                        ))}
                    </TableBody>

                    {!!pending?.length && (
                        <>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('status_pending')}</TableCell>
                                    <TableCell></TableCell>
                                    <TableCell
                                        sx={{textAlign: 'right'}}
                                    >
                                        {t('amount')}
                                    </TableCell>
                                    <TableCell>{t('due_date')}</TableCell>
                                    <TableCell> </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {pending.map(i => (
                                    <TableRow
                                        key={i.id}
                                        sx={{
                                            '&:last-child td, &:last-child th': {border: 0},
                                        }}
                                    >
                                        <TableCell>
                                            <InvoiceLink invoice={i}/>
                                        </TableCell>
                                        <TableCell>
                                            {i.type === 'freight' && (
                                                <Link href={`/voyage/${i.voyage.id}/finances`}>{i.voyage.number}</Link>
                                            )}
                                        </TableCell>
                                        <TableCell
                                            sx={{textAlign: 'right'}}
                                        >
                                            <Currency value={i.grandTotal} currency={i.currency || undefined}/>
                                        </TableCell>
                                        <TableCell><Date date={i.dueDate}/></TableCell>

                                        <TableCell>
                                            {my.type === CompanyType.Master && i.payer.name}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </>
                    )}
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default BalanceAndInvoices;
